import { AxiosRequest } from "_services/api";
import { toastErro } from "components/Toast";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DirecaoOrdenacao,
  IOrdenacao,
} from "../../SolicitacaoPlaca/interfaces/IOrdenacao";
import { mensagemDeErro } from "../../SolicitacaoPlaca/utils/mensagemDeErro";
import {
  FILTROS_LOTES_TERCEIROS_VAZIOS,
  ORDENACAO_LOTES_TERCEIROS_PADRAO,
} from "../constants/filtrosLotesTerceiros";
import { IFiltrosLotesTerceiros } from "../interfaces/IFiltrosLotesTerceiros";
import { ILoteTerceiros } from "../interfaces/ILoteTerceiros";
import { exportarLotesTerceirosXlsx } from "../utils/exportarLotesTerceirosXlsx";
import { montarParametrosLotesTerceiros } from "../utils/montarParametrosLotesTerceiros";
import { nomeArquivoDoContentDisposition } from "../utils/nomeArquivoDoContentDisposition";

const LIMITE_PADRAO = 50;
const LIMITE_TODAS_AS_PAGINAS = -1;
const STATUS_NAO_ENCONTRADO = 404;

export type ResultadoRemocaoLote = "removido" | "naoEncontrado" | "falha";

export function useLotesTerceiros() {
  const msAw = useMemo(
    () => AxiosRequest.makeApi(process.env.REACT_APP_MS_AW),
    []
  );
  const [filtros, setFiltros] = useState<IFiltrosLotesTerceiros>(
    FILTROS_LOTES_TERCEIROS_VAZIOS
  );
  const [pagina, setPagina] = useState(1);
  const [limite, setLimite] = useState(LIMITE_PADRAO);
  const [ordenacao, setOrdenacao] = useState<IOrdenacao>(
    ORDENACAO_LOTES_TERCEIROS_PADRAO
  );
  const [versao, setVersao] = useState(0);
  const [reiniciarPaginacao, setReiniciarPaginacao] = useState(false);
  const [lotes, setLotes] = useState<ILoteTerceiros[]>([]);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [removendo, setRemovendo] = useState(false);
  const [baixando, setBaixando] = useState<number | null>(null);
  const ultimaBusca = useRef(0);

  useEffect(() => {
    const busca = ++ultimaBusca.current;

    async function buscar() {
      setCarregando(true);
      try {
        const parametros = montarParametrosLotesTerceiros({
          filtros,
          pagina,
          limite,
          ordenacao,
        });
        const { data } = await msAw.get(
          `/solicitacao-placa/lote?${parametros}`
        );
        if (busca !== ultimaBusca.current) return;
        setLotes(data?.itens ?? []);
        setTotalRegistros(data?.meta?.total ?? 0);
      } catch (erro) {
        if (busca !== ultimaBusca.current) return;
        setLotes([]);
        setTotalRegistros(0);
        toastErro(mensagemDeErro(erro, "Erro ao buscar os lotes"));
      } finally {
        if (busca === ultimaBusca.current) setCarregando(false);
      }
    }

    buscar();
  }, [msAw, filtros, pagina, limite, ordenacao, versao]);

  const voltarParaPrimeiraPagina = useCallback(() => {
    setPagina(1);
    setReiniciarPaginacao((valor) => !valor);
  }, []);

  const aplicarFiltros = useCallback(
    (novosFiltros: IFiltrosLotesTerceiros) => {
      setFiltros(novosFiltros);
      setVersao((valor) => valor + 1);
      voltarParaPrimeiraPagina();
    },
    [voltarParaPrimeiraPagina]
  );

  const mudarLimite = useCallback(
    (novoLimite: number) => {
      setLimite(novoLimite);
      voltarParaPrimeiraPagina();
    },
    [voltarParaPrimeiraPagina]
  );

  const ordenar = useCallback((campo: string, direcao: DirecaoOrdenacao) => {
    setOrdenacao({ campo, direcao });
    setPagina(1);
  }, []);

  const recarregar = useCallback(() => setVersao((valor) => valor + 1), []);

  const exportar = useCallback(async () => {
    setExportando(true);
    try {
      const parametros = montarParametrosLotesTerceiros({
        filtros,
        pagina: 1,
        limite: LIMITE_TODAS_AS_PAGINAS,
        ordenacao,
      });
      const { data } = await msAw.get(`/solicitacao-placa/lote?${parametros}`);
      exportarLotesTerceirosXlsx(data?.itens ?? []);
    } catch (erro) {
      toastErro(mensagemDeErro(erro, "Erro ao exportar os lotes"));
    } finally {
      setExportando(false);
    }
  }, [msAw, filtros, ordenacao]);

  const remover = useCallback(
    async (id: number): Promise<ResultadoRemocaoLote> => {
      setRemovendo(true);
      try {
        await msAw.delete(`/solicitacao-placa/lote/${id}`);
        recarregar();
        return "removido";
      } catch (erro: any) {
        toastErro(mensagemDeErro(erro, "Erro ao remover o lote"));
        if (erro?.response?.status !== STATUS_NAO_ENCONTRADO) return "falha";
        recarregar();
        return "naoEncontrado";
      } finally {
        setRemovendo(false);
      }
    },
    [msAw, recarregar]
  );

  // Com responseType blob, o corpo do erro não é JSON: as mensagens são fixas.
  const baixar = useCallback(
    async (id: number) => {
      setBaixando(id);
      try {
        const { data, headers } = await msAw.get(
          `/solicitacao-placa/lote/${id}/arquivo`,
          { responseType: "blob" }
        );
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          nomeArquivoDoContentDisposition(
            headers?.["content-disposition"],
            `lote_${id}.xlsx`
          )
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (erro: any) {
        toastErro(
          erro?.response?.status === STATUS_NAO_ENCONTRADO
            ? "Excel do lote não encontrado."
            : "Não foi possível baixar o Excel do lote."
        );
      } finally {
        setBaixando(null);
      }
    },
    [msAw]
  );

  return {
    lotes,
    totalRegistros,
    carregando,
    limite,
    reiniciarPaginacao,
    aplicarFiltros,
    setPagina,
    mudarLimite,
    ordenar,
    recarregar,
    exportar,
    exportando,
    remover,
    removendo,
    baixar,
    baixando,
  };
}
